import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TColumnName, TDataset } from "../types";
import { Camera, PlotData } from "plotly.js";
import {
  HISTOGRAM_LAYOUT,
  MARKER_STYLE_INITIAL,
  PLOT_LAYOUT,
} from "../constants";
import { Figure } from "react-plotly.js";
import {
  calculateHistogramDimensions,
  calculateScatterPlotDimensions,
} from "../utils/window-utils";

type TLeftPanel = {
  selectedDataset?: string;
  selectedMethod?: TColumnName;
  selectedColumn?: TColumnName;
  columnsForHover: TColumnName[];
};

export type TRightPanel = {
  visible: boolean;
  // data point
  clickedPoint: TDataset[0] | undefined;
  // styling
  markerOpacity: number;
  markerSize: number;
  markerLineColor: string;
  showAxes: boolean;
  showCorrelations: boolean;
  showProjectionX: boolean;
  showProjectionY: boolean;
  showProjectionZ: boolean;
  // functions
  showDensityPlot: boolean;
  sdFilter: number;
  sdMin: number;
  sdMax: number;
  // transforms
  applyZScore: boolean;
  applyMinMaxScaling: boolean;
  categoricalColumnsAsContinuous: Record<string, Array<string>>;
  applyPCA: boolean;
  projectToPCA: boolean;
  projectToPCAInProgress: boolean;
  showPCAAxis: boolean;
  // debug
  camera: Partial<Camera> | undefined;
};

export type TComputedValues = {
  standardDeviations: Record<string, number>;
  means: Record<string, number>;
  smallestValue: Record<string, number>;
  largestValue: Record<string, number>;
};

type TInitState = {
  rows: TDataset;
  conScores: Record<string, Record<string, string>>;
  leftPanel: TLeftPanel;
  rightPanel: TRightPanel;
  methodColumns: TColumnName[];
  continuousColumns: TColumnName[];
  categoricalColumns: TColumnName[];
  continuousPredictionColumns: TColumnName[];
  categoricalPredictionColumns: TColumnName[];
  metaColumns: TColumnName[];
  graphData: Partial<PlotData>[];
  scatterLayout: Partial<Figure["layout"]>;
  densityPlotData: Partial<PlotData>[];
  histogramData: Partial<PlotData>[];
  histogramLayout: Figure["layout"];

  computedValues: TComputedValues;

  // state when graph is rendered
  // used for redrawing
  renderedState: {
    leftPanel: TLeftPanel;
    rightPanel: TRightPanel;
  };

  errors: string[] | null;
};

const rightPanelInitialState: TRightPanel = {
  visible: true,
  clickedPoint: undefined,
  markerLineColor: MARKER_STYLE_INITIAL.line.color,
  markerSize: MARKER_STYLE_INITIAL.size,
  markerOpacity: MARKER_STYLE_INITIAL.opacity,
  showAxes: PLOT_LAYOUT.scene?.xaxis?.visible ?? false,
  showCorrelations: true,
  showDensityPlot: false,
  showProjectionX: false,
  showProjectionY: false,
  showProjectionZ: false,
  sdFilter: 3,
  sdMin: 0,
  sdMax: 0,
  categoricalColumnsAsContinuous: {},
  applyZScore: false,
  applyMinMaxScaling: false,
  applyPCA: false,
  projectToPCA: false,
  projectToPCAInProgress: false,
  showPCAAxis: false,
  camera: undefined,
};

const initialState: TInitState = {
  rows: [],
  conScores: {},
  methodColumns: [],
  continuousColumns: [],
  categoricalColumns: [],
  metaColumns: [],
  continuousPredictionColumns: [],
  categoricalPredictionColumns: [],
  leftPanel: {
    selectedDataset: undefined,
    selectedMethod: undefined,
    selectedColumn: undefined,
    columnsForHover: [],
  },
  rightPanel: rightPanelInitialState,
  graphData: [],
  scatterLayout: {},
  histogramData: [],
  densityPlotData: [],
  histogramLayout: {
    ...HISTOGRAM_LAYOUT,
    ...calculateHistogramDimensions(calculateScatterPlotDimensions(true).width),
  },

  computedValues: {
    standardDeviations: {},
    means: {},
    smallestValue: {},
    largestValue: {},
  },

  renderedState: {
    leftPanel: {
      selectedDataset: undefined,
      selectedMethod: undefined,
      selectedColumn: undefined,
      columnsForHover: [],
    },
    rightPanel: rightPanelInitialState,
  },

  errors: null,
};

const rootSlice = createSlice({
  name: "root",
  initialState,
  reducers: {
    unselectDataset: (state) => ({
      ...initialState,
    }),

    initialise: (state, _payload: PayloadAction<string>) => ({
      ...initialState,
    }),

    initialiseDone: (
      state,
      action: PayloadAction<
        Pick<
          TInitState,
          | "rows"
          | "conScores"
          | "methodColumns"
          | "categoricalColumns"
          | "continuousColumns"
          | "continuousPredictionColumns"
          | "categoricalPredictionColumns"
          | "metaColumns"
          | "leftPanel"
          | "computedValues"
        >
      >
    ) => {
      return {
        ...state,
        errors: null,
        ...action.payload,
        leftPanel: {
          ...state.leftPanel,
          ...action.payload.leftPanel,
        },
        rightPanel: {
          ...state.rightPanel,
          clickedPoint: undefined,
        },
      };
    },

    initialiseError: (state, action: PayloadAction<string[]>) => ({
      ...state,
      errors: action.payload,
    }),

    setSelectedDataset: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        leftPanel: {
          ...state.leftPanel,
          selectedDataset: action.payload,
        },
      };
    },

    setSelectedMethod: (state, action: PayloadAction<TColumnName>) => {
      return {
        ...state,
        leftPanel: {
          ...state.leftPanel,
          selectedMethod: action.payload,
        },
      };
    },

    setSelectedColumn: (state, action: PayloadAction<TColumnName>) => {
      return {
        ...state,
        leftPanel: {
          ...state.leftPanel,
          selectedColumn: action.payload,
        },
      };
    },

    setSelectedMethodAndColumn: (
      state,
      action: PayloadAction<{
        method: TColumnName;
        column: TColumnName;
      }>
    ) => {
      return {
        ...state,
        leftPanel: {
          ...state.leftPanel,
          selectedMethod: action.payload.method,
          selectedColumn: action.payload.column,
        },
      };
    },

    loadPrevOrNextMethod: (state, _action: PayloadAction<"prev" | "next">) => ({
      ...state,
    }),

    loadPrevOrNextColumnSaga: (
      state,
      _action: PayloadAction<"prev" | "next">
    ) => ({
      ...state,
    }),

    setColumnsForHover: (state, action: PayloadAction<TColumnName[]>) => {
      return {
        ...state,
        leftPanel: {
          ...state.leftPanel,
          columnsForHover: action.payload,
        },
      };
    },

    setScatterPlotData: (
      state,
      action: PayloadAction<Partial<PlotData>[]>
    ) => ({
      ...state,
      graphData: action.payload,
      renderedState: {
        leftPanel: state.leftPanel,
        rightPanel: state.rightPanel,
      },
    }),

    setDensityPlotData: (
      state,
      action: PayloadAction<Partial<PlotData>[]>
    ) => ({
      ...state,
      densityPlotData: action.payload,
    }),

    setHistogramData: (state, action: PayloadAction<Partial<PlotData>[]>) => ({
      ...state,
      histogramData: action.payload,
    }),

    generateGraph: (
      state,
      _action: PayloadAction<undefined | { range?: [number, number] }>
    ) => state,

    toggleRightPanel: (state) => {
      return {
        ...state,
        rightPanel: {
          ...state.rightPanel,
          visible: !state.rightPanel.visible,
        },
      };
    },

    findClickedPoint: (state, _action: PayloadAction<string>) => state,

    setClickedPoint: (state, action: PayloadAction<TDataset[0]>) => {
      return {
        ...state,
        rightPanel: {
          ...state.rightPanel,
          clickedPoint: action.payload,
        },
      };
    },

    setRightPanel: (
      state,
      action: PayloadAction<Partial<typeof state["rightPanel"]>>
    ) => {
      return {
        ...state,
        rightPanel: {
          ...state.rightPanel,
          ...action.payload,
        },
      };
    },

    setShowAxes: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        rightPanel: {
          ...state.rightPanel,
          showAxes: action.payload,
        },
      };
    },

    loadDatasetFromFile: (state, _action: PayloadAction<File>) => state,

    setHistogramLayout: (
      state,
      action: PayloadAction<Partial<Figure["layout"]>>
    ) => {
      return {
        ...state,
        histogramLayout: {
          ...state.histogramLayout,
          ...action.payload,
        },
      };
    },

    convertToContinuous: (state, _action: PayloadAction<Array<string>>) =>
      state,

    convertToContinuousDone: (
      state,
      action: PayloadAction<
        Pick<
          TInitState,
          "continuousColumns" | "categoricalColumns" | "rows" | "computedValues"
        > & {
          categoricalColumnsAsContinuous: TRightPanel["categoricalColumnsAsContinuous"];
        }
      >
    ) => ({
      ...state,
      continuousColumns: action.payload.continuousColumns,
      categoricalColumns: action.payload.categoricalColumns,
      rows: action.payload.rows,
      computedValues: action.payload.computedValues,
      rightPanel: {
        ...state.rightPanel,
        categoricalColumnsAsContinuous:
          action.payload.categoricalColumnsAsContinuous,
      },
    }),

    applyReorder: (state, _action: PayloadAction<Array<string>>) => state,

    applyReorderDone: (
      state,
      action: PayloadAction<
        Pick<TInitState, "rows"> & {
          categoricalColumnsAsContinuous: TRightPanel["categoricalColumnsAsContinuous"];
        }
      >
    ) => ({
      ...state,
      rows: action.payload.rows,
      rightPanel: {
        ...state.rightPanel,
        categoricalColumnsAsContinuous:
          action.payload.categoricalColumnsAsContinuous,
      },
    }),

    revertToCategorical: (state) => state,
    revertToCategoricalDone: (
      state,
      action: PayloadAction<
        Pick<
          TInitState,
          "rows" | "continuousColumns" | "categoricalColumns"
        > & {
          categoricalColumnsAsContinuous: TRightPanel["categoricalColumnsAsContinuous"];
        }
      >
    ) => ({
      ...state,
      rows: action.payload.rows,
      continuousColumns: action.payload.continuousColumns,
      categoricalColumns: action.payload.categoricalColumns,
      rightPanel: {
        ...state.rightPanel,
        categoricalColumnsAsContinuous:
          action.payload.categoricalColumnsAsContinuous,
      },
    }),

    projectToPCA: (state) => ({
      ...state,
      rightPanel: {
        ...state.rightPanel,
        projectToPCAInProgress: true,
      },
    }),

    projectToPCADone: (state) => ({
      ...state,
      rightPanel: {
        ...state.rightPanel,
        projectToPCA: true,
        projectToPCAInProgress: false,
      },
    }),

    rotateHeatMap: (
      state,
      _action: PayloadAction<{ x: number; y: number; z: number }>
    ) => state,
  },
});

export const rootActions = rootSlice.actions;
export default rootSlice;
